import { getFormattedNumber } from './l10n';

const FILE_SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB', 'TB'] as const;

/**
 * Formats a byte count as a human-readable file size (e.g. `20 MB`), scaling
 * to the largest unit where the value is still >= 1. Used to keep file-size
 * limits and errors readable instead of showing a raw byte count.
 */
export const formatFileSize = (bytes: number, locale?: string): string => {
  if (bytes < 1024) {
    return `${getFormattedNumber({ locale, value: bytes })} ${FILE_SIZE_UNITS[0]}`;
  }

  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    FILE_SIZE_UNITS.length - 1,
  );

  return `${getFormattedNumber({ locale, value: bytes / 1024 ** exponent })} ${FILE_SIZE_UNITS[exponent]}`;
};

/**
 * Downloads a file. Takes a blob, which it converts to an object URL, or a URL string (blob: or https:).
 * HTTP(S) URLs are fetched as blobs first so browsers respect the requested file name.
 *
 * @param data - The blob or URL to be downloaded.
 * @param fileName - The name of the file to be downloaded including the extension.
 */
export const downloadFile = (
  data: Blob | string,
  fileName: string,
): Promise<void> => {
  return getDownloadUrl(data).then(({ fileURL, shouldRevokeUrl }) => {
    triggerDownload(fileURL, fileName);

    // Clean up memory if the URL was created with URL.createObjectURL.
    if (shouldRevokeUrl) {
      globalThis.URL.revokeObjectURL(fileURL);
    }
  });
};

const INVALID_FILENAME_CHARACTER_REGEX = /[\\/:*?"<>|]/g;

/**
 * Strips characters illegal in file names and collapses whitespace, capping the
 * length at `maxLength` (default 180) to stay well under the common 255-byte
 * filesystem / zip-entry limit once a folder prefix and extension are added.
 */
export const sanitizeFileName = (value: string, maxLength = 180): string =>
  value
    .replace(INVALID_FILENAME_CHARACTER_REGEX, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength);

/**
 * Detects whether a file starts with ZIP magic bytes ("PK", 0x50 0x4b).
 *
 * Useful when the requested file extension cannot be trusted, for example when
 * an API may return either a single PDF ("%PDF") or a ZIP bundle for the same
 * export request.
 */
export function isZipSignature(bytes: Uint8Array) {
  return bytes[0] === 0x50 && bytes[1] === 0x4b;
}

async function getDownloadUrl(
  data: Blob | string,
): Promise<{ fileURL: string; shouldRevokeUrl: boolean }> {
  if (typeof data !== 'string') {
    return {
      fileURL: globalThis.URL.createObjectURL(data),
      shouldRevokeUrl: true,
    };
  }

  if (data.startsWith('blob:')) {
    return {
      fileURL: data,
      shouldRevokeUrl: false,
    };
  }

  try {
    const response = await fetch(data);

    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.status}`);
    }

    return {
      fileURL: globalThis.URL.createObjectURL(await response.blob()),
      shouldRevokeUrl: true,
    };
  } catch {
    return {
      fileURL: data,
      shouldRevokeUrl: false,
    };
  }
}

function triggerDownload(fileURL: string, fileName: string): void {
  const anchor = document.createElement('a');
  anchor.href = fileURL;
  anchor.download = fileName || 'export';
  document.body.append(anchor); // Required to work in FireFox
  anchor.click();
}
